import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { validarToken, redefinirSenha } from '../../services/senhaService';
import './RedefinirSenha.css';

type PageState = 'loading' | 'error' | 'form' | 'success';

interface SenhaCriterios {
  minCaracteres: boolean;
  maiuscula: boolean;
  minuscula: boolean;
  numero: boolean;
}

function verificarCriterios(senha: string): SenhaCriterios {
  return {
    minCaracteres: senha.length >= 8,
    maiuscula: /[A-Z]/.test(senha),
    minuscula: /[a-z]/.test(senha),
    numero: /[0-9]/.test(senha),
  };
}

function RedefinirSenha() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [criterios, setCriterios] = useState<SenhaCriterios>({
    minCaracteres: false,
    maiuscula: false,
    minuscula: false,
    numero: false,
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMessage('Token não fornecido. Solicite a redefinição novamente.');
      setPageState('error');
      return;
    }

    validarToken(token).then((resultado) => {
      if (resultado.valido) {
        setPageState('form');
      } else {
        setErrorMessage(
          resultado.mensagem || 'Token inválido ou expirado. Solicite a redefinição novamente.',
        );
        setPageState('error');
      }
    });
  }, [token]);

  function handleSenhaChange(valor: string) {
    setNovaSenha(valor);
    setCriterios(verificarCriterios(valor));
    if (formError) setFormError('');
  }

  function handleConfirmacaoChange(valor: string) {
    setConfirmacao(valor);
    if (formError) setFormError('');
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // Validação client-side
    const c = verificarCriterios(novaSenha);
    if (!c.minCaracteres || !c.maiuscula || !c.minuscula || !c.numero) {
      setFormError('A senha não atende a todos os critérios.');
      return;
    }

    if (novaSenha !== confirmacao) {
      setFormError('As senhas não coincidem.');
      return;
    }

    setFormError('');
    setIsSubmitting(true);

    const resultado = await redefinirSenha(token, novaSenha);

    if (resultado.sucesso) {
      navigate('/', { state: { mensagemSucesso: 'Senha redefinida com sucesso! Faça login com sua nova senha.' } });
    } else {
      setFormError(resultado.mensagem || 'Erro ao redefinir senha.');
    }

    setIsSubmitting(false);
  }

  if (pageState === 'loading') {
    return (
      <div className="redefinir-senha-page">
        <div className="redefinir-senha-card">
          <div className="redefinir-senha-loading">
            <p>Validando token...</p>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="redefinir-senha-page">
        <div className="redefinir-senha-card">
          <header className="redefinir-senha-card__header">
            <h1 className="redefinir-senha-card__title">Link Inválido</h1>
          </header>
          <div className="redefinir-senha-error-container">
            <p className="redefinir-senha-error-message">{errorMessage}</p>
            <Link to="/esqueci-senha" className="redefinir-senha-link">
              Solicitar nova redefinição
            </Link>
          </div>
          <footer className="redefinir-senha-footer">
            <Link to="/" className="redefinir-senha-footer__back">
              ← Voltar para login
            </Link>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="redefinir-senha-page">
      <div className="redefinir-senha-card">
        <header className="redefinir-senha-card__header">
          <h1 className="redefinir-senha-card__title">Redefinir Senha</h1>
          <p className="redefinir-senha-card__subtitle">
            Defina sua nova senha abaixo
          </p>
        </header>

        <form className="redefinir-senha-form" onSubmit={handleSubmit} noValidate>
          {formError && (
            <div className="redefinir-senha-form__error--general" role="alert">
              {formError}
            </div>
          )}

          <div className="redefinir-senha-form__field">
            <label className="redefinir-senha-form__label" htmlFor="nova-senha">
              Nova senha
            </label>
            <input
              id="nova-senha"
              className="redefinir-senha-form__input"
              type="password"
              placeholder="Mínimo 8 caracteres"
              maxLength={64}
              value={novaSenha}
              onChange={(e) => handleSenhaChange(e.target.value)}
              autoComplete="new-password"
              aria-describedby="criterios-senha"
            />
          </div>

          <div className="redefinir-senha-criterios" id="criterios-senha" aria-live="polite">
            <p className="redefinir-senha-criterios__titulo">Critérios da senha:</p>
            <ul className="redefinir-senha-criterios__lista">
              <li className={criterios.minCaracteres ? 'criterio--atendido' : 'criterio--pendente'}>
                <span className="criterio-icon">{criterios.minCaracteres ? '✓' : '○'}</span>
                Mínimo 8 caracteres
              </li>
              <li className={criterios.maiuscula ? 'criterio--atendido' : 'criterio--pendente'}>
                <span className="criterio-icon">{criterios.maiuscula ? '✓' : '○'}</span>
                Pelo menos 1 letra maiúscula
              </li>
              <li className={criterios.minuscula ? 'criterio--atendido' : 'criterio--pendente'}>
                <span className="criterio-icon">{criterios.minuscula ? '✓' : '○'}</span>
                Pelo menos 1 letra minúscula
              </li>
              <li className={criterios.numero ? 'criterio--atendido' : 'criterio--pendente'}>
                <span className="criterio-icon">{criterios.numero ? '✓' : '○'}</span>
                Pelo menos 1 número
              </li>
            </ul>
          </div>

          <div className="redefinir-senha-form__field">
            <label className="redefinir-senha-form__label" htmlFor="confirmar-senha">
              Confirmar nova senha
            </label>
            <input
              id="confirmar-senha"
              className="redefinir-senha-form__input"
              type="password"
              placeholder="Repita a nova senha"
              maxLength={64}
              value={confirmacao}
              onChange={(e) => handleConfirmacaoChange(e.target.value)}
              autoComplete="new-password"
              aria-invalid={confirmacao.length > 0 && novaSenha !== confirmacao}
            />
            {confirmacao.length > 0 && novaSenha !== confirmacao && (
              <span className="redefinir-senha-form__error" role="alert">
                As senhas não coincidem
              </span>
            )}
          </div>

          <button
            type="submit"
            className="redefinir-senha-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Redefinindo...' : 'Redefinir Senha'}
          </button>
        </form>

        <footer className="redefinir-senha-footer">
          <Link to="/" className="redefinir-senha-footer__back">
            ← Voltar para login
          </Link>
        </footer>
      </div>
    </div>
  );
}

export default RedefinirSenha;
